import React, { useState, useEffect } from "react";
import {
  Container,
  Title,
  Card,
  Text,
  Button,
  Group,
  Badge,
  Grid,
  Stack,
  Divider,
  NumberInput,
  TextInput,
  Textarea,
  Avatar,
  Box,
  Stepper,
  Center,
  Loader,
  Paper,
  SimpleGrid,
  useMantineTheme,
  BackgroundImage,
  rem,
  Accordion,
  Checkbox,
  MultiSelect,
  Alert,
  Anchor,
  Transition,
} from "@mantine/core";
import {
  IconClock,
  IconCurrencyDollar,
  IconMapPin,
  IconUsers,
  IconGlass,
  IconInfoCircle,
  IconUser,
  IconPhone,
  IconMail,
  IconMap2,
  IconCheck,
  IconBuilding,
  IconArrowLeft,
  IconCalendar,
  IconCreditCard,
  IconShieldCheck,
  IconWorld,
  IconCalendarEvent,
  IconCurrencyRupee,
  IconDownload,
  IconPackage,
  IconPlus,
  IconReceipt,
  IconStar,
} from "@tabler/icons-react";
import { DatePicker } from "@mantine/dates";
import { useForm, isEmail, isNotEmpty, matches } from "@mantine/form";
import { addDoc, collection, doc, getDoc } from "firebase/firestore";
import { db } from "../../../firebase/FirebaseCredential";
import { Helmet } from "react-helmet-async";
import { useParams } from "react-router-dom";

// Define types for our data structures
interface Facility {
  id: number;
  name: string;
  type: "gym" | "spa" | "turf";
  logo: string;
  description: string;
  requiresPayment: boolean;
  classes?: GymClass[];
  services?: SpaService[];
  courts?: TurfCourt[];
  background: string;
  location: string;
  companyDetails: CompanyDetails;
  meta_data: MetaData;
}

interface CompanyDetails {
  name: string;
  address: string;
  phone: string;
  email: string;
  website: string;
  description: string;
}

interface MetaData {
  title: string;
  description: string;
  keywords: string[];
}

interface GymClass {
  id: number;
  name: string;
  trainer: string;
  duration: number;
  price: number;
  capacity: number;
  time: string;
  description: string;
}

interface SpaService {
  id: number;
  name: string;
  duration: number;
  price: number;
  description: string;
  category: string;
}

interface TurfCourt {
  id: number;
  name: string;
  type: string;
  pricePerHour: number;
  available: boolean;
  size: string;
  description: string;
}

// Mock API data with company details and meta data
// const mockFacilities: Facility[] = [
//     {
//       id: 1,
//       name: "Fitness Center",
//       type: "gym",
//       logo: "https://images.unsplash.com/photo-1534438327276-14e5300c3a48?w=100",
//       description: "State-of-the-art fitness center with professional trainers",
//       requiresPayment: true,
//       background: "",
//       //   background:
//       //     "https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1770&q=80",
//       location: "123 Fitness Street, Health City",
//       companyDetails: {
//         name: "Fitness Center Inc.",
//         address: "123 Fitness Street, Health City, FC 12345",
//         phone: "+1 (555) 123-4567",
//         email: "info@fitnesscenter.com",
//         website: "www.fitnesscenter.com",
//         description:
//           "Premium fitness center with state-of-the-art equipment and professional trainers",
//       },
//       meta_data: {
//         title: "Fitness Center - Premium Gym Facilities | Book Online",
//         description:
//           "Book your gym classes at our state-of-the-art fitness center with professional trainers. Yoga, HIIT, Strength Training and more.",
//         keywords: [
//           "gym",
//           "fitness",
//           "workout",
//           "yoga",
//           "HIIT",
//           "strength training",
//         ],
//       },
//       classes: [
//         {
//           id: 1,
//           name: "Yoga & Meditation",
//           trainer: "Sarah Johnson",
//           duration: 60,
//           price: 15,
//           capacity: 20,
//           time: "07:00 AM",
//           description:
//             "Start your day with peace and flexibility. Suitable for all levels.",
//         },
//         {
//           id: 2,
//           name: "HIIT Training",
//           trainer: "Mike Rodriguez",
//           duration: 45,
//           price: 20,
//           capacity: 15,
//           time: "05:30 PM",
//           description:
//             "High-intensity interval training to boost your metabolism.",
//         },
//         {
//           id: 3,
//           name: "Strength Training",
//           trainer: "David Wilson",
//           duration: 50,
//           price: 18,
//           capacity: 12,
//           time: "06:30 PM",
//           description:
//             "Build muscle and increase strength with guided weight training.",
//         },
//       ],
//     },
//     {
//       id: 2,
//       name: "Luxury Spa",
//       type: "spa",
//       logo: "https://images.unsplash.com/photo-1544161515-4ab6ce6db874?w=100",
//       description: "Relax and rejuvenate with our premium spa treatments",
//       requiresPayment: true,
//       background: "",
//       location: "456 Relaxation Road, Wellness City",
//       companyDetails: {
//         name: "Luxury Spa & Wellness",
//         address: "456 Relaxation Road, Wellness City, SP 67890",
//         phone: "+1 (555) 987-6543",
//         email: "relax@luxuryspa.com",
//         website: "www.luxuryspa.com",
//         description:
//           "Premium spa offering a wide range of relaxation and beauty treatments",
//       },
//       meta_data: {
//         title: "Luxury Spa - Premium Spa Treatments | Book Online",
//         description:
//           "Book your spa treatments at our luxury spa. Massages, facials, body treatments and more for complete relaxation and rejuvenation.",
//         keywords: [
//           "spa",
//           "massage",
//           "facial",
//           "wellness",
//           "relaxation",
//           "beauty treatments",
//         ],
//       },
//       services: [
//         {
//           id: 1,
//           name: "Swedish Massage",
//           duration: 60,
//           price: 80,
//           category: "Massage",
//           description:
//             "Gentle full-body massage to promote relaxation and improve circulation.",
//         },
//         {
//           id: 2,
//           name: "Deep Tissue Massage",
//           duration: 75,
//           price: 100,
//           category: "Massage",
//           description:
//             "Targets deeper muscle layers to relieve chronic pain and tension.",
//         },
//         {
//           id: 3,
//           name: "Hot Stone Massage",
//           duration: 75,
//           price: 95,
//           category: "Massage",
//           description:
//             "Uses heated stones to relax muscles and allow for deeper pressure.",
//         },
//         {
//           id: 4,
//           name: "Aromatherapy Massage",
//           duration: 60,
//           price: 90,
//           category: "Massage",
//           description:
//             "Massage combined with essential oils to enhance mood and relaxation.",
//         },
//         {
//           id: 5,
//           name: "Classic Facial",
//           duration: 50,
//           price: 70,
//           category: "Facials & Skin Treatments",
//           description:
//             "Cleansing, exfoliation, and hydration for refreshed, glowing skin.",
//         },
//         {
//           id: 6,
//           name: "Anti-Aging Facial",
//           duration: 60,
//           price: 95,
//           category: "Facials & Skin Treatments",
//           description:
//             "Reduces fine lines and restores youthful radiance to the skin.",
//         },
//         {
//           id: 7,
//           name: "Body Scrub (Salt / Sugar)",
//           duration: 45,
//           price: 65,
//           category: "Body Treatments",
//           description:
//             "Exfoliates dead skin cells, leaving skin smooth and refreshed.",
//         },
//         {
//           id: 8,
//           name: "Body Wrap (Mud / Seaweed)",
//           duration: 60,
//           price: 85,
//           category: "Body Treatments",
//           description: "Detoxifying wrap that nourishes and tightens the skin.",
//         },
//         {
//           id: 9,
//           name: "Hair Spa (Hydration, Keratin, Protein)",
//           duration: 45,
//           price: 55,
//           category: "Hair & Scalp Care",
//           description:
//             "Deep conditioning treatment to restore shine and strength to hair.",
//         },
//         {
//           id: 10,
//           name: "Scalp Massage & Oil Therapy",
//           duration: 30,
//           price: 40,
//           category: "Hair & Scalp Care",
//           description:
//             "Relieves stress, improves blood circulation, and nourishes hair roots.",
//         },
//         {
//           id: 11,
//           name: "Manicure (Classic / Spa / Gel)",
//           duration: 40,
//           price: 35,
//           category: "Hand & Foot Care",
//           description:
//             "Cleansing, trimming, and polishing to keep hands and nails elegant.",
//         },
//         {
//           id: 12,
//           name: "Pedicure (Classic / Spa / Deluxe)",
//           duration: 50,
//           price: 45,
//           category: "Hand & Foot Care",
//           description:
//             "Relaxing foot care treatment with exfoliation and nail grooming.",
//         },
//         {
//           id: 13,
//           name: "Reiki Healing",
//           duration: 60,
//           price: 100,
//           category: "Wellness & Special Therapies",
//           description:
//             "Energy healing technique that promotes relaxation and balance.",
//         },
//         {
//           id: 14,
//           name: "Shirodhara (Oil Drip Therapy)",
//           duration: 70,
//           price: 120,
//           category: "Wellness & Special Therapies",
//           description:
//             "Ayurvedic therapy where warm oil is gently poured on the forehead.",
//         },
//         {
//           id: 15,
//           name: "Head Massage (15–30 mins)",
//           duration: 30,
//           price: 25,
//           category: "Add-ons / Quick Services",
//           description:
//             "Quick stress-relieving massage focusing on scalp and temples.",
//         },
//         {
//           id: 16,
//           name: "Express Facial",
//           duration: 30,
//           price: 45,
//           category: "Add-ons / Quick Services",
//           description:
//             "Quick cleansing and hydration treatment for an instant glow.",
//         },
//       ],
//     },
//   {
//     id: 3,
//     name: "Sports Complex",
//     type: "turf",
//     logo: "https://images.unsplash.com/photo-1552667466-07770ae110d0?w=100",
//     description: "Professional sports turf for various activities",
//     requiresPayment: true,
//     background: "",
//     //   "https://images.unsplash.com/photo-1552667466-07770ae110d0?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1770&q=80",
//     location: "789 Sports Avenue, Activity City",
//     companyDetails: {
//       name: "Sports Complex Ltd.",
//       address: "789 Sports Avenue, Activity City, SC 11223",
//       phone: "+1 (555) 456-7890",
//       email: "bookings@sportscomplex.com",
//       website: "www.sportscomplex.com",
//       description:
//         "Professional sports facilities with multiple turf courts for various sports",
//     },
//     meta_data: {
//       title: "Sports Complex - Turf Booking | Reserve Your Court",
//       description:
//         "Book turf courts at our sports complex. Football, cricket, and other sports facilities available for booking at competitive rates.",
//       keywords: [
//         "turf",
//         "sports",
//         "football",
//         "cricket",
//         "sports complex",
//         "court booking",
//       ],
//     },
//     courts: [
//       {
//         id: 1,
//         name: "Court A - Football",
//         type: "Football",
//         pricePerHour: 80,
//         available: true,
//         size: "11v11",
//         description:
//           "Full-size football field with artificial turf and professional markings.",
//       },
//       {
//         id: 2,
//         name: "Court B - Cricket",
//         type: "Cricket",
//         pricePerHour: 70,
//         available: true,
//         size: "Practice Nets",
//         description: "Cricket practice nets with bowling machine support.",
//       },
//     ],
//   },
// ];

// Available time slots grouped by time of day
const timeSlots = {
  morning: ["08:00 AM", "09:00 AM", "10:00 AM", "11:00 AM"],
  afternoon: ["12:00 PM", "01:00 PM", "02:00 PM", "03:00 PM", "04:00 PM"],
  evening: ["05:00 PM", "06:00 PM", "07:00 PM", "08:00 PM"],
};

const FacilityBookingPage: React.FC = () => {
  const { branchId } = useParams();

  const [activeFacility, setActiveFacility] = useState<Facility | null>(null);
  const [selectedService, setSelectedService] = useState<any>(null);
  const [selectedServices, setSelectedServices] = useState<any[]>([]); // For multi-select
  const [activeStep, setActiveStep] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const theme = useMantineTheme();

  const form = useForm({
    initialValues: {
      facilityId: 0,
      serviceIds: [] as number[],
      date: null as Date | null,
      time: "",
      duration: 1,
      userName: "",
      userEmail: "",
      userPhone: "",
      userLocation: "",
      userNotes: "",
    },

    validate: {
      userName: isNotEmpty("Name is required"),
      userEmail: isEmail("Invalid email"),
      userPhone: matches(/^[+]?[\d\s-()]{10,}$/, "Invalid phone number"),
      serviceIds: (value) =>
        value.length === 0 ? "At least one service is required" : null,
      date: (value, values) =>
        activeFacility?.type !== "gym" && !value ? "Date is required" : null,
      time: (value, values) =>
        activeFacility?.type !== "gym" && !value ? "Time is required" : null,
    },
  });
  useEffect(() => {
    if (!branchId) return;

    const fetchBookingData = async () => {
      try {
        setIsLoading(true);
        const ref = doc(db, "bookingpublicurls", branchId);
        const snapshot = await getDoc(ref);

        if (snapshot.exists()) {
          setActiveFacility(snapshot.data() as any);
          setIsLoading(false);
        } else {
          console.log("No booking data found for this branch");
        }
      } catch (error) {
        console.error("Error fetching booking data:", error);
      }
    };

    fetchBookingData();
  }, [branchId]);
  // Set meta tags for SEO
  //   useEffect(() => {
  //     if (activeFacility) {
  //       document.title = activeFacility.meta_data.title;

  //       const metaDescription = document.querySelector(
  //         'meta[name="description"]'
  //       );
  //       if (metaDescription) {
  //         metaDescription.setAttribute(
  //           "content",
  //           activeFacility.meta_data.description
  //         );
  //       }

  //       const metaKeywords = document.querySelector('meta[name="keywords"]');
  //       if (metaKeywords) {
  //         metaKeywords.setAttribute(
  //           "content",
  //           activeFacility.meta_data.keywords.join(", ")
  //         );
  //       }
  //     }
  //   }, [activeFacility]);

  // Simulate API call to fetch facility data
  // useEffect(() => {
  //   const fetchFacilityData = async () => {
  //     setIsLoading(true);
  //     // In a real app, this would be an API call with the facility ID
  //     await new Promise((resolve) => setTimeout(resolve, 1000));

  //     // For demo purposes, let's use the first facility
  //     setActiveFacility(mockFacilities[0]);
  //     setIsLoading(false);
  //   };

  //   fetchFacilityData();
  // }, []);

  const handleServiceSelect = (service: any) => {
    if (
      activeFacility?.type === "spa" ||
      activeFacility?.type === "gym" ||
      activeFacility?.type === "turf"
    ) {
      // For spa, gym, and turf, allow multi-select
      const isSelected = selectedServices.some((s) => s.id === service.id);
      let newSelectedServices;

      if (isSelected) {
        newSelectedServices = selectedServices.filter(
          (s) => s.id !== service.id
        );
      } else {
        newSelectedServices = [...selectedServices, service];
      }

      setSelectedServices(newSelectedServices);
      form.setFieldValue(
        "serviceIds",
        newSelectedServices.map((s) => s.id)
      );
    } else {
      // For other facility types (if any), single select
      setSelectedService(service);
      setSelectedServices([service]);
      form.setFieldValue("serviceIds", [service.id]);
    }

    form.setFieldValue("facilityId", activeFacility?.id || 0);
  };

  const isServiceSelected = (service: any) => {
    return selectedServices.some((s) => s.id === service.id);
  };

  const handleNextStep = () => {
    // Validate current step before proceeding
    if (activeStep === 0) {
      const serviceErrors = form.validateField("serviceIds");
      if (serviceErrors.hasError) return;
    } else if (activeStep === 1 && activeFacility?.type !== "gym") {
      // Validate date and time for non-gym facilities
      const dateErrors = form.validateField("date");
      if (dateErrors.hasError) return;

      const timeErrors = form.validateField("time");
      if (timeErrors.hasError) return;
    } else if (activeStep === (activeFacility?.type === "gym" ? 1 : 2)) {
      // Validate user details - check all required fields
      const nameErrors = form.validateField("userName");
      const emailErrors = form.validateField("userEmail");
      const phoneErrors = form.validateField("userPhone");

      if (nameErrors.hasError || emailErrors.hasError || phoneErrors.hasError)
        return;
    }

    setActiveStep(activeStep + 1);
  };

  const handlePreviousStep = () => {
    setActiveStep(activeStep - 1);
  };

  const saveBookingToFirestore = async () => {
    try {
      setSubmitting(true);

      const bookingData = {
        facility: {
          id: activeFacility?.id,
          name: activeFacility?.name,
          type: activeFacility?.type,
        },
        services: selectedServices,
        date: form.values.date,
        time: form.values.time,
        duration: form.values.duration,
        userName: form.values.userName,
        userEmail: form.values.userEmail,
        userPhone: form.values.userPhone,
        userNotes: form.values.userNotes,
        total: calculateTotal(),
        createdAt: new Date(),
        status: "confirmed",
      };

      // Add a new document with a generated id
      const docRef = await addDoc(collection(db, "bookings"), bookingData);
      console.log("Document written with ID: ", docRef.id);
    } catch (error) {
      console.error("Error adding document: ", error);
      alert("There was an error processing your booking. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleBookingSubmit = () => {
    saveBookingToFirestore();
  };

  const groupServicesByCategory = (services: SpaService[]) => {
    return services.reduce((acc: Record<string, SpaService[]>, service) => {
      if (!acc[service.category]) {
        acc[service.category] = [];
      }
      acc[service.category].push(service);
      return acc;
    }, {});
  };

  const calculateTotal = () => {
    if (!selectedServices.length) return 0;

    let total = 0;

    if (activeFacility?.type === "turf") {
      total = selectedServices.reduce(
        (sum, service) => sum + service.pricePerHour * form.values.duration,
        0
      );
    } else {
      total = selectedServices.reduce((sum, service) => sum + service.price, 0);
    }

    return total;
  };

  const renderServiceSelection = () => {
    if (!activeFacility) return null;

    return (
      <Box>
        <Title order={2} mb="lg">
          Select {activeFacility.type === "spa" ? "Services" : "Service"}
        </Title>

        {activeFacility.type === "gym" && (
          <Grid>
            {activeFacility.classes?.map((classItem) => (
              <Grid.Col key={classItem.id} md={6} lg={4}>
                <Card
                  padding="lg"
                  radius="md"
                  withBorder
                  style={{
                    cursor: "pointer",
                    borderColor: isServiceSelected(classItem)
                      ? theme.colors.blue[6]
                      : undefined,
                    transition: "transform 0.2s",
                  }}
                  onClick={() => handleServiceSelect(classItem)}
                >
                  <Group position="apart" mb="xs">
                    <Text fw={500}>{classItem.name}</Text>
                    <Badge variant="light">₹{classItem.price}</Badge>
                  </Group>

                  <Text size="sm" c="dimmed" mb="md">
                    {classItem.description}
                  </Text>

                  <Group mb="xs">
                    <IconClock size={16} />
                    <Text size="sm">
                      {classItem.duration} mins • {classItem.time}
                    </Text>
                  </Group>

                  <Group mb="xs">
                    <IconUsers size={16} />
                    <Text size="sm">Trainer: {classItem.trainer}</Text>
                  </Group>

                  <Group mb="md">
                    <IconInfoCircle size={16} />
                    <Text size="sm">Capacity: {classItem.capacity} people</Text>
                  </Group>

                  <Checkbox
                    checked={isServiceSelected(classItem)}
                    onChange={() => handleServiceSelect(classItem)}
                    label={
                      isServiceSelected(classItem) ? "Selected" : "Select Class"
                    }
                    mt="md"
                  />
                </Card>
              </Grid.Col>
            ))}
          </Grid>
        )}

        {activeFacility.type === "spa" && activeFacility.services && (
          <Accordion
            variant="contained"
            defaultValue={
              Object.keys(groupServicesByCategory(activeFacility.services))[0]
            }
          >
            {Object.entries(
              groupServicesByCategory(activeFacility.services)
            ).map(([category, services]) => (
              <Accordion.Item key={category} value={category}>
                <Accordion.Control>
                  <Text fw={500}>{category}</Text>
                </Accordion.Control>
                <Accordion.Panel>
                  <Grid>
                    {services.map((service) => (
                      <Grid.Col key={service.id} md={6} lg={4}>
                        <Card
                          padding="lg"
                          radius="md"
                          withBorder
                          style={{
                            cursor: "pointer",
                            borderColor: isServiceSelected(service)
                              ? theme.colors.pink[6]
                              : undefined,
                            transition: "transform 0.2s",
                          }}
                          onClick={() => handleServiceSelect(service)}
                        >
                          <Group position="apart" mb="xs">
                            <Text fw={500}>{service.name}</Text>
                            <Badge color="pink" variant="light">
                              ₹{service.price}
                            </Badge>
                          </Group>

                          <Text size="sm" c="dimmed" mb="md">
                            {service.description}
                          </Text>

                          <Group mb="xs">
                            <IconClock size={16} />
                            <Text size="sm">{service.duration} minutes</Text>
                          </Group>

                          <Checkbox
                            checked={isServiceSelected(service)}
                            onChange={() => handleServiceSelect(service)}
                            label={
                              isServiceSelected(service) ? "Selected" : "Select"
                            }
                            mt="md"
                          />
                        </Card>
                      </Grid.Col>
                    ))}
                  </Grid>
                </Accordion.Panel>
              </Accordion.Item>
            ))}
          </Accordion>
        )}

        {activeFacility.type === "turf" && (
          <Grid>
            {activeFacility.courts?.map((court) => (
              <Grid.Col key={court.id} md={6} lg={4}>
                <Card
                  padding="lg"
                  radius="md"
                  withBorder
                  style={{
                    cursor: court.available ? "pointer" : "not-allowed",
                    borderColor: isServiceSelected(court)
                      ? theme.colors.green[6]
                      : undefined,
                    transition: "transform 0.2s",
                    opacity: court.available ? 1 : 0.6,
                  }}
                  onClick={() => court.available && handleServiceSelect(court)}
                >
                  <Group position="apart" mb="xs">
                    <Text fw={500}>{court.name}</Text>
                    <Badge color={court.available ? "green" : "red"}>
                      {court.available ? "Available" : "Booked"}
                    </Badge>
                  </Group>

                  <Text size="sm" c="dimmed" mb="md">
                    {court.description}
                  </Text>

                  <Group mb="xs">
                    <IconGlass size={16} />
                    <Text size="sm">Type: {court.type}</Text>
                  </Group>

                  <Group mb="xs">
                    <IconMapPin size={16} />
                    <Text size="sm">Size: {court.size}</Text>
                  </Group>

                  <Group mb="md">
                    <IconCurrencyDollar size={16} />
                    <Text size="sm">₹{court.pricePerHour} per hour</Text>
                  </Group>

                  <Checkbox
                    checked={isServiceSelected(court)}
                    onChange={() =>
                      court.available && handleServiceSelect(court)
                    }
                    label={
                      isServiceSelected(court) ? "Selected" : "Select Court"
                    }
                    mt="md"
                    disabled={!court.available}
                  />
                </Card>
              </Grid.Col>
            ))}
          </Grid>
        )}

        {form.errors.serviceIds && (
          <Alert color="red" mt="md">
            {form.errors.serviceIds}
          </Alert>
        )}

        <Group position="right" mt="xl">
          <Button
            onClick={handleNextStep}
            disabled={selectedServices.length === 0}
          >
            Continue
          </Button>
        </Group>
      </Box>
    );
  };

  const renderDateTimeSelection = () => {
    return (
      <Box>
        <Title order={2} mb="lg">
          Select Date & Time
        </Title>

        <Grid>
          <Grid.Col md={6}>
            <Text fw={500} mb="sm">
              Select Date
            </Text>
            <DatePicker
              value={form.values.date}
              onChange={(date) => form.setFieldValue("date", date)}
              minDate={new Date()}
            />
            {form.errors.date && (
              <Text color="red" size="sm" mt={4}>
                {form.errors.date}
              </Text>
            )}
          </Grid.Col>

          <Grid.Col md={6}>
            <Text fw={500} mb="sm">
              Select Time
            </Text>
            <Box>
              <Text fw={500} size="sm" mb="xs">
                Morning
              </Text>
              <SimpleGrid cols={2} spacing="sm" mb="md">
                {timeSlots.morning.map((time) => (
                  <Button
                    key={time}
                    variant={form.values.time === time ? "filled" : "outline"}
                    color={form.values.time === time ? "blue" : "gray"}
                    onClick={() => form.setFieldValue("time", time)}
                    size="sm"
                  >
                    {time}
                  </Button>
                ))}
              </SimpleGrid>

              <Text fw={500} size="sm" mb="xs">
                Afternoon
              </Text>
              <SimpleGrid cols={2} spacing="sm" mb="md">
                {timeSlots.afternoon.map((time) => (
                  <Button
                    key={time}
                    variant={form.values.time === time ? "filled" : "outline"}
                    color={form.values.time === time ? "blue" : "gray"}
                    onClick={() => form.setFieldValue("time", time)}
                    size="sm"
                  >
                    {time}
                  </Button>
                ))}
              </SimpleGrid>

              <Text fw={500} size="sm" mb="xs">
                Evening
              </Text>
              <SimpleGrid cols={2} spacing="sm">
                {timeSlots.evening.map((time) => (
                  <Button
                    key={time}
                    variant={form.values.time === time ? "filled" : "outline"}
                    color={form.values.time === time ? "blue" : "gray"}
                    onClick={() => form.setFieldValue("time", time)}
                    size="sm"
                  >
                    {time}
                  </Button>
                ))}
              </SimpleGrid>
            </Box>
            {form.errors.time && (
              <Text color="red" size="sm" mt={4}>
                {form.errors.time}
              </Text>
            )}
          </Grid.Col>
        </Grid>

        {activeFacility?.type === "turf" && (
          <Box mt="lg">
            <Text fw={500} mb="sm">
              Duration (hours)
            </Text>
            <NumberInput
              value={form.values.duration}
              onChange={(value) => form.setFieldValue("duration", value || 1)}
              min={1}
              max={8}
            />
          </Box>
        )}

        <Group position="apart" mt="xl">
          <Button variant="default" onClick={handlePreviousStep}>
            Back
          </Button>
          <Button onClick={handleNextStep}>Continue</Button>
        </Group>
      </Box>
    );
  };
  //    console.log(form);
  const renderUserDetails = () => {
    return (
      <Box>
        <Title order={2} mb="lg">
          Your Information
        </Title>

        <Grid>
          <Grid.Col md={6}>
            <TextInput
              label="Full Name"
              placeholder="Enter your full name"
              value={form.values.userName}
              onChange={(e) => form.setFieldValue("userName", e.target.value)}
              icon={<IconUser size={16} />}
              error={form.errors.userName}
              required
            />
          </Grid.Col>

          <Grid.Col md={6}>
            <TextInput
              label="Email Address"
              placeholder="Enter your email"
              type="email"
              value={form.values.userEmail}
              onChange={(e) => form.setFieldValue("userEmail", e.target.value)}
              required
              icon={<IconMail size={16} />}
              error={form.errors.userEmail}
            />
          </Grid.Col>

          <Grid.Col md={6}>
            <TextInput
              label="Phone Number"
              placeholder="Enter your phone number"
              value={form.values.userPhone}
              onChange={(e) => form.setFieldValue("userPhone", e.target.value)}
              required
              icon={<IconPhone size={16} />}
              error={form.errors.userPhone}
            />
          </Grid.Col>

          <Grid.Col span={12}>
            <Textarea
              label="Special Requests"
              placeholder="Any special requirements?"
              value={form.values.userNotes}
              onChange={(e) => form.setFieldValue("userNotes", e.target.value)}
            />
          </Grid.Col>
        </Grid>

        <Group position="apart" mt="xl">
          <Button variant="default" onClick={handlePreviousStep}>
            Back
          </Button>
          <Button onClick={handleNextStep}>Continue</Button>
        </Group>
      </Box>
    );
  };

  const renderBookingSummary = () => {
    if (!activeFacility || selectedServices.length === 0) return null;

    return (
      <Container size="lg" py="xl">
        <Stack spacing="xl">
          {/* Header */}
          <Box ta="center">
            <Title order={1} mb="xs">
              Booking Confirmation
            </Title>
            <Text c="dimmed" size="lg">
              Please review your booking details below
            </Text>
          </Box>

          <Grid gutter="xl">
            {/* Main Content */}
            <Grid.Col md={8}>
              <Stack spacing="lg">
                {/* Services Card */}
                <Card padding="lg" radius="md" withBorder>
                  <Card.Section withBorder inheritPadding py="xs">
                    <Group position="apart">
                      <Title order={3}>Selected Services</Title>
                      <Badge variant="light">
                        {selectedServices.length} item
                        {selectedServices.length > 1 ? "s" : ""}
                      </Badge>
                    </Group>
                  </Card.Section>

                  <Stack spacing="sm" mt="md">
                    {selectedServices.map((service, index) => (
                      <Box key={index} p="sm" bg="gray.0">
                        <Group position="apart" align="center">
                          <Box>
                            <Text fw={600} size="md">
                              {service.name}
                            </Text>
                            {service.description && (
                              <Text size="sm" c="dimmed">
                                {service.description}
                              </Text>
                            )}
                          </Box>
                          <Box ta="right">
                            <Text fw={700} size="lg">
                              ₹
                              {activeFacility.type === "turf"
                                ? service.pricePerHour * form.values.duration
                                : service.price}
                            </Text>
                            {activeFacility.type === "turf" && (
                              <Text size="xs" c="dimmed">
                                ₹{service.pricePerHour}/hour ×{" "}
                                {form.values.duration}h
                              </Text>
                            )}
                          </Box>
                        </Group>
                      </Box>
                    ))}

                    <Divider my="sm" />

                    <Group position="apart" p="sm">
                      <Text fw={700} size="lg">
                        Total Amount
                      </Text>
                      <Text fw={700} size="xl">
                        ₹{calculateTotal()}
                      </Text>
                    </Group>
                  </Stack>
                </Card>

                {/* Booking Details Card */}
                <Card padding="lg" radius="md" withBorder>
                  <Card.Section withBorder inheritPadding py="xs">
                    <Title order={3}>Booking Details</Title>
                  </Card.Section>

                  <SimpleGrid cols={2} spacing="md" mt="md">
                    {activeFacility.type !== "gym" && (
                      <>
                        <Box>
                          <Text size="sm" fw={600} c="dimmed" mb={4}>
                            DATE
                          </Text>
                          <Group spacing="xs">
                            <IconCalendar
                              size={16}
                              color="var(--mantine-color-blue-6)"
                            />
                            <Text fw={500}>
                              {form.values.date?.toLocaleDateString()}
                            </Text>
                          </Group>
                        </Box>
                        <Box>
                          <Text size="sm" fw={600} c="dimmed" mb={4}>
                            TIME
                          </Text>
                          <Group spacing="xs">
                            <IconClock
                              size={16}
                              color="var(--mantine-color-blue-6)"
                            />
                            <Text fw={500}>{form.values.time}</Text>
                          </Group>
                        </Box>
                      </>
                    )}

                    {activeFacility.type === "gym" && (
                      <Box>
                        <Text size="sm" fw={600} c="dimmed" mb={4}>
                          CLASS TIME
                        </Text>
                        <Group spacing="xs">
                          <IconClock
                            size={16}
                            color="var(--mantine-color-blue-6)"
                          />
                          <Text fw={500}>{selectedServices[0].time}</Text>
                        </Group>
                      </Box>
                    )}

                    {activeFacility.type === "turf" && (
                      <Box>
                        <Text size="sm" fw={600} c="dimmed" mb={4}>
                          DURATION
                        </Text>
                        <Group spacing="xs">
                          <IconClock
                            size={16}
                            color="var(--mantine-color-blue-6)"
                          />
                          <Text fw={500}>{form.values.duration} hours</Text>
                        </Group>
                      </Box>
                    )}
                  </SimpleGrid>
                </Card>

                {/* Customer Information Card */}
                <Card padding="lg" radius="md" withBorder>
                  <Card.Section withBorder inheritPadding py="xs">
                    <Title order={3}>Customer Information</Title>
                  </Card.Section>

                  <Stack spacing="sm" mt="md">
                    <Group spacing="sm">
                      <IconUser size={18} color="var(--mantine-color-blue-6)" />
                      <Text fw={500}>{form.values.userName}</Text>
                    </Group>
                    <Group spacing="sm">
                      <IconMail size={18} color="var(--mantine-color-blue-6)" />
                      <Text>{form.values.userEmail}</Text>
                    </Group>
                    <Group spacing="sm">
                      <IconPhone
                        size={18}
                        color="var(--mantine-color-blue-6)"
                      />
                      <Text>{form.values.userPhone}</Text>
                    </Group>

                    {form.values.userNotes && (
                      <Box mt="sm" p="sm" bg="yellow.0">
                        <Text size="sm" fw={600} mb={4} c="yellow.8">
                          Special Requests:
                        </Text>
                        <Text size="sm" fs="italic" c="yellow.9">
                          {form.values.userNotes}
                        </Text>
                      </Box>
                    )}
                  </Stack>
                </Card>
              </Stack>
            </Grid.Col>

            {/* Sidebar */}
            <Grid.Col md={4}>
              <Stack spacing="lg">
                {/* Facility Information Card */}
                <Card padding="lg" radius="md" withBorder>
                  <Card.Section withBorder inheritPadding py="xs">
                    <Title order={3}>Facility Information</Title>
                  </Card.Section>

                  <Stack spacing="md" mt="md">
                    <Box>
                      <Text size="sm" fw={600} c="dimmed" mb={4}>
                        LOCATION
                      </Text>
                      <Group spacing="xs" align="flex-start">
                        <IconMapPin
                          size={16}
                          color="var(--mantine-color-blue-6)"
                          style={{ marginTop: 2 }}
                        />
                        <Text size="sm">{activeFacility.location}</Text>
                      </Group>
                    </Box>

                    <Divider />

                    <Box>
                      <Text size="sm" fw={600} c="dimmed" mb={8}>
                        FACILITY PROVIDER
                      </Text>
                      <Stack spacing="xs">
                        <Group spacing="xs">
                          <IconBuilding
                            size={16}
                            color="var(--mantine-color-blue-6)"
                          />
                          <Text fw={600} size="sm">
                            {activeFacility.companyDetails.name}
                          </Text>
                        </Group>
                        <Text size="xs" c="dimmed">
                          {activeFacility.companyDetails.address}
                        </Text>
                        <Group spacing="xs">
                          <IconPhone
                            size={14}
                            color="var(--mantine-color-gray-6)"
                          />
                          <Text size="xs">
                            {activeFacility.companyDetails.phone}
                          </Text>
                        </Group>
                        <Group spacing="xs">
                          <IconMail
                            size={14}
                            color="var(--mantine-color-gray-6)"
                          />
                          <Text size="xs">
                            {activeFacility.companyDetails.email}
                          </Text>
                        </Group>
                        {activeFacility.companyDetails.website && (
                          <Group spacing="xs">
                            <IconWorld
                              size={14}
                              color="var(--mantine-color-gray-6)"
                            />
                            <Anchor
                              size="xs"
                              href={activeFacility.companyDetails.website}
                              target="_blank"
                            >
                              Website
                            </Anchor>
                          </Group>
                        )}
                      </Stack>
                    </Box>

                    {activeFacility.companyDetails.description && (
                      <>
                        <Divider />
                        <Box>
                          <Text size="xs" c="dimmed" lineClamp={3}>
                            {activeFacility.companyDetails.description}
                          </Text>
                        </Box>
                      </>
                    )}
                  </Stack>
                </Card>

                {/* Payment Information Card */}
                <Card padding="lg" radius="md" withBorder bg="green.0">
                  <Group spacing="xs" mb="sm">
                    <IconShieldCheck
                      size={20}
                      color="var(--mantine-color-green-6)"
                    />
                    <Text fw={600} c="green.8">
                      Payment Information
                    </Text>
                  </Group>
                  <Text size="sm" c="green.7">
                    {activeFacility.requiresPayment
                      ? "Payment will be processed securely in the next step"
                      : "No payment required - booking will be confirmed instantly"}
                  </Text>
                </Card>
              </Stack>
            </Grid.Col>
          </Grid>

          {/* Action Buttons */}
          <Card padding="lg" radius="md" withBorder>
            <Group position="apart">
              <Button
                variant="subtle"
                leftIcon={<IconArrowLeft size={16} />}
                onClick={handlePreviousStep}
                size="md"
              >
                Back to Details
              </Button>
              <Button
                onClick={handleBookingSubmit}
                loading={submitting}
                size="md"
                rightIcon={
                  activeFacility.requiresPayment ? (
                    <IconCreditCard size={16} />
                  ) : (
                    <IconCheck size={16} />
                  )
                }
              >
                {activeFacility.requiresPayment
                  ? "Proceed to Payment"
                  : "Confirm Booking"}
              </Button>
            </Group>
          </Card>
        </Stack>
      </Container>
    );
  };

  if (isLoading) {
    return (
      <Container size="xl" py="xl">
        <Center style={{ height: "50vh" }}>
          <Loader size="lg" />
        </Center>
      </Container>
    );
  }

  // Determine step labels based on facility type
  const getStepLabels = () => {
    if (activeFacility?.type === "gym") {
      return ["Select Class", "Your Information", "Booking Summary"];
    }
    return [
      "Select Service",
      "Date & Time",
      "Your Information",
      "Booking Summary",
    ];
  };

  const stepLabels = getStepLabels();
  const getStepDescription = (index: number) => {
    const descriptions = [
      "Choose your preferred services",

      activeFacility?.type === "gym"
        ? "Enter your details"
        : "Select date and time",
      activeFacility?.type === "gym"
        ? "Review and confirm"
        : "Enter your details",
      "Review and confirm",
    ];
    return descriptions[index];
  };

  return (
    <Box
      style={{
        minHeight: "100vh",

        padding: rem(20),
      }}
    >
      <Helmet>
        <title>{activeFacility?.meta_data?.title || "Facility Booking"}</title>
        <meta
          name="description"
          content={
            activeFacility?.meta_data.description ||
            "Book your facility services online"
          }
        />
        <meta
          name="keywords"
          content={
            activeFacility?.meta_data.keywords?.join(", ") ||
            "booking, facility, services"
          }
        />
      </Helmet>
      {/* Enhanced Facility Header */}
      <Container size="xl" mb="xl">
        <Card
          padding="xl"
          radius="lg"
          style={{
            background: "rgba(255, 255, 255, 0.95)",
            backdropFilter: "blur(10px)",
            border: "1px solid rgba(255, 255, 255, 0.2)",
          }}
        >
          <Group spacing="lg" align="center">
            <Box>
              <Avatar
                src={activeFacility?.logo || ""}
                size={80}
                radius="50%"
                style={{
                  border: "3px solid white",
                }}
              />
            </Box>

            <Box style={{ flex: 1 }}>
              <Group spacing="sm" mb="xs">
                <Title
                  order={1}
                  style={{
                    fontSize: rem(32),
                    fontWeight: 700,
                  }}
                >
                  {activeFacility?.name}
                </Title>
                <Badge size="lg" style={{ textTransform: "uppercase" }}>
                  {activeFacility?.type}
                </Badge>
              </Group>

              {activeFacility?.description && (
                <Text size="lg" c="dimmed" style={{ maxWidth: 600 }}>
                  {activeFacility.description}
                </Text>
              )}

              {activeFacility?.location && (
                <Group spacing="xs" mt="sm">
                  <IconMapPin size={16} color={theme.colors.gray[6]} />
                  <Text size="sm" c="dimmed">
                    {activeFacility.location}
                  </Text>
                </Group>
              )}
            </Box>
          </Group>
        </Card>
      </Container>

      {/* Main Booking Content */}
      <Container size="xl">
        <Card
          padding={0}
          radius="xl"
          style={{
            background: "rgba(255, 255, 255, 0.98)",
            backdropFilter: "blur(15px)",
            border: "1px solid rgba(255, 255, 255, 0.3)",
            overflow: "hidden",
          }}
        >
          <Box>
            {/* Enhanced Stepper */}
            <Box
              p="xl"
              style={{
                background: "linear-gradient(135deg, #f8f9ff 0%, #e8f0fe 100%)",
                borderBottom: "1px solid rgba(0, 0, 0, 0.05)",
              }}
            >
              <Stepper
                active={activeStep}
                breakpoint="sm"
                size="lg"
                styles={{
                  step: {
                    padding: rem(16),
                  },

                  stepDescription: {
                    fontSize: rem(14),
                    color: theme.colors.gray[6],
                    marginTop: rem(4),
                  },
                  stepLabel: {
                    fontSize: rem(16),
                    fontWeight: 600,
                    color: theme.colors.gray[8],
                  },
                  separator: {
                    height: rem(3),
                    backgroundColor: theme.colors.gray[2],
                  },
                }}
              >
                {stepLabels.map((label, index) => (
                  <Stepper.Step
                    key={index}
                    label={label}
                    description={getStepDescription(index)}
                  />
                ))}
              </Stepper>
            </Box>

            {/* Step Content with Animation Container */}
            <Box
              p="xl"
              style={{
                minHeight: "60vh",
                position: "relative",
              }}
            >
              <Transition
                mounted={true}
                transition="slide-up"
                duration={300}
                timingFunction="ease"
              >
                {(styles) => (
                  <Box style={styles}>
                    {activeStep === 0 && renderServiceSelection()}
                    {activeStep === 1 &&
                      activeFacility?.type !== "gym" &&
                      renderDateTimeSelection()}
                    {activeStep === (activeFacility?.type === "gym" ? 1 : 2) &&
                      renderUserDetails()}
                    {activeStep === (activeFacility?.type === "gym" ? 2 : 3) &&
                      renderBookingSummary()}
                  </Box>
                )}
              </Transition>
            </Box>
          </Box>
        </Card>
      </Container>
    </Box>
  );
};

export default FacilityBookingPage;
